import axios, { AxiosError } from 'axios';
import * as R from 'ramda';
import { CustomError, NetworkError, TechnicalError } from '../../common/errors';
import Result from '../../common/result';
import { HardcodedConfigResolver, Resolver } from '../../config';
import { Token, TokenResult } from '../common/token';
import {
  AuthenticationService,
  AuthenticateExchangeTokenResult,
  AuthenticateRequest,
  Authentication,
  AuthenticationResult,
  CreateExchangeTokenRequest,
  ExchangeToken,
  ExchangeTokenRequest,
  ExchangeTokenResult,
  UserFeedbackRequest,
  UserFeedbackResult,
  NonceResult,
  Nonce,
} from './authenticationService';

export default class AuthenticationServiceImplementation implements AuthenticationService {
  protected authEndpoint: string;

  // the civic pass endpoint
  protected endpoint: string;

  private feedbackEndpointToken: string;

  private token?: Token;

  private defaultRetries: number;

  private customHeaders: Record<string, string>;

  constructor() {
    const serviceConfig = Resolver.getServiceConfig();
    this.authEndpoint = serviceConfig.validation.authEndpoint;
    this.endpoint = serviceConfig.validation.validationEndpoint;
    this.defaultRetries = serviceConfig.networkRetryConfig.defaultRetries;
    this.customHeaders = {};
    this.feedbackEndpointToken = serviceConfig.validation.feedbackToken;
  }

  setToken(token: Token): void {
    this.token = token;
  }

  setCustomHeaders(headers: Record<string, string>): void {
    this.customHeaders = R.clone(headers);
  }

  private async getToken(forPath: string): Promise<TokenResult> {
    if (!this.token) {
      return Result.failed(new TechnicalError(`Error calling ${forPath}: Missing auth token`));
    }

    return this.token.getToken();
  }

  async submitUserFeedback(request: UserFeedbackRequest): Promise<UserFeedbackResult> {
    const url = `${this.endpoint}/feedback/${request.networkAddress}`;
    try {
      await axios.request<void>({
        method: 'POST',
        url,
        data: {
          ...R.omit(['networkAddress'], request),
        },
        headers: R.mergeDeepLeft(
          {
            'x-civic-feedback-token': this.feedbackEndpointToken,
          },
          this.customHeaders,
        ),
        retryConfig: {
          retries: 0,
        },
      });
      return Result.noValueSuccess();
    } catch (error) {
      return AuthenticationServiceImplementation.getErrorResult(url, error);
    }
  }

  async authenticate(request: AuthenticateRequest): Promise<AuthenticationResult> {
    const { chain } = request;
    const { authEndpoint } = HardcodedConfigResolver.loadConfig(chain).services.validation;
    const isIcp = chain === 'icp';
    const url = isIcp ? `${authEndpoint}/authenticate-icp` : `${authEndpoint}/authenticate`;
    try {
      const response = await axios.request<Authentication>({
        method: 'POST',
        url,
        data: {
          ...request,
        },
        headers: this.customHeaders,
        retryConfig: {
          retries: this.defaultRetries,
        },
      });
      const { token } = response.data;
      if (typeof token !== 'string') {
        return Result.failed(new TechnicalError(`Unexpected result fetching token: ${JSON.stringify(response.data)}`));
      }

      return Result.successful(response.data);
    } catch (error) {
      return AuthenticationServiceImplementation.getErrorResult(url, error);
    }
  }

  async authenticateExchangeToken(request: ExchangeTokenRequest): Promise<AuthenticateExchangeTokenResult> {
    const url = `${this.endpoint}/authenticate/token/${request.exchangeToken}/activate`;
    try {
      const response = await axios.request<ExchangeToken>({
        method: 'POST',
        url,
        data: {},
        headers: this.customHeaders,
        retryConfig: {
          enableFailurePrompt: true,
        },
      });
      const { token } = response.data;
      if (typeof token !== 'string') {
        return Result.failed(
          new TechnicalError(`Unexpected result authenticating exchange token: ${JSON.stringify(response.data)}`),
        );
      }

      return Result.successful(response.data);
    } catch (error) {
      const errorResponse = (error as AxiosError)?.response;
      if (errorResponse) {
        return Result.failed(new NetworkError(errorResponse.data.message, errorResponse.status, error as AxiosError));
      }
      return AuthenticationServiceImplementation.getErrorResult(url, error);
    }
  }

  async requestExchangeToken(request: CreateExchangeTokenRequest): Promise<ExchangeTokenResult> {
    const url = `${this.endpoint}/authenticate/token`;
    const tokenResult = await this.getToken(url);
    if (!tokenResult.isSuccessful()) {
      return Result.failed(tokenResult.failure());
    }
    try {
      const response = await axios.request<ExchangeToken>({
        method: 'POST',
        url,
        data: {
          ...request,
        },
        headers: R.mergeDeepLeft(
          {
            Authorization: `Bearer ${tokenResult.success()}`,
          },
          this.customHeaders,
        ),
        retryConfig: {
          enableFailurePrompt: true,
        },
      });
      return Result.successful(response.data);
    } catch (error) {
      return AuthenticationServiceImplementation.getErrorResult(url, error);
    }
  }

  async getExchangeToken(request: ExchangeTokenRequest): Promise<ExchangeTokenResult> {
    const url = `${this.endpoint}/authenticate/token/${request.exchangeToken}`;
    const tokenResult = await this.getToken(url);
    if (!tokenResult.isSuccessful()) {
      return Result.failed(tokenResult.failure());
    }
    try {
      const response = await axios.request<ExchangeToken>({
        method: 'GET',
        url,
        data: {
          ...request,
        },
        headers: R.mergeDeepLeft(
          {
            Authorization: `Bearer ${tokenResult.success()}`,
          },
          this.customHeaders,
        ),
        retryConfig: {
          retries: this.defaultRetries,
        },
      });
      return Result.successful(response.data);
    } catch (error) {
      return AuthenticationServiceImplementation.getErrorResult(url, error);
    }
  }

  async getNonce(): Promise<NonceResult> {
    const url = `${this.authEndpoint}/nonce`;

    try {
      const response = await axios.request<Nonce>({
        method: 'GET',
        url,
        headers: this.customHeaders,
        retryConfig: {
          retries: this.defaultRetries,
        },
      });
      return Result.successful(response.data);
    } catch (error) {
      return AuthenticationServiceImplementation.getErrorResult(url, error);
    }
  }

  private static getErrorResult<TSuccess = boolean>(url: string, error: unknown): Result<CustomError, TSuccess> {
    const statusCode = (error as AxiosError)?.response?.status;
    return Result.failed(new NetworkError(`Network error calling ${url}`, statusCode || 500, error as Error));
  }
}
